import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Dumbbell, ArrowLeft, Lock, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MUSCLES = ['chest', 'back', 'legs', 'shoulders', 'arms', 'core', 'cardio'];
const MODALITIES = ['strength', 'cardio', 'mobility'];

// Split "1. ... 2. ... 3. ..." into one line per numbered step
function NumberedText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(?=\d+\.\s)/).map(s => s.trim()).filter(Boolean);
  return (
    <div className="space-y-1">
      {parts.map((p, i) => <p key={i} className="text-sm text-stone font-inter">{p}</p>)}
    </div>
  );
}

const LABELS = {
  es: {
    title: 'Biblioteca de ejercicios', back: 'Volver a Entrenamiento',
    search: 'Buscar ejercicio...', all: 'Todos', total: 'ejercicios',
    empty: 'No hay ejercicios. Pide al administrador que genere la biblioteca.',
    premiumOnly: 'La biblioteca de ejercicios es solo para usuarios Premium.', upgrade: 'Mejorar a Premium',
    muscles: { chest: 'Pecho', back: 'Espalda', legs: 'Pierna', shoulders: 'Hombro', arms: 'Brazo', core: 'Core', cardio: 'Cardio' },
    modalities: { strength: 'Fuerza', cardio: 'Cardio', mobility: 'Movilidad' },
    instructions: 'Instrucciones', mistakes: 'Errores comunes', equipment: 'Material', difficulty: 'Dificultad',
  },
  en: {
    title: 'Exercise library', back: 'Back to Training',
    search: 'Search exercise...', all: 'All', total: 'exercises',
    empty: 'No exercises yet. Ask the admin to generate the library.',
    premiumOnly: 'The exercise library is for Premium users only.', upgrade: 'Upgrade to Premium',
    muscles: { chest: 'Chest', back: 'Back', legs: 'Legs', shoulders: 'Shoulders', arms: 'Arms', core: 'Core', cardio: 'Cardio' },
    modalities: { strength: 'Strength', cardio: 'Cardio', mobility: 'Mobility' },
    instructions: 'Instructions', mistakes: 'Common mistakes', equipment: 'Equipment', difficulty: 'Difficulty',
  },
};

export default function ExercisesPage() {
  const { language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const L = LABELS[language] || LABELS.es;
  const isPremium = user?.plan === 'premium';

  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [muscle, setMuscle] = useState('');
  const [modality, setModality] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    fetchExercises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, muscle, modality]);

  const fetchExercises = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (muscle) params.set('muscle', muscle);
      if (modality) params.set('modality', modality);
      const res = await fetch(`${API}/exercises?${params.toString()}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setExercises(data.exercises || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-primary" /></div>
          <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">{L.title}</h1>
          <p className="text-stone font-inter mb-6">{L.premiumOnly}</p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full bg-primary">{L.upgrade}</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/training')} className="flex items-center gap-2 text-stone hover:text-primary mb-4 font-inter">
          <ArrowLeft className="w-4 h-4" /> {L.back}
        </button>
        <div className="flex items-center gap-3 mb-6">
          <Dumbbell className="w-7 h-7 text-primary" />
          <h1 className="font-manrope font-bold text-3xl text-charcoal">{L.title}</h1>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') fetchExercises(); }} placeholder={L.search} className="pl-10 h-11 rounded-xl border-border bg-white/50" />
            </div>
            <Button variant="outline" onClick={fetchExercises} className="h-11 rounded-xl">{L.search.replace('...', '')}</Button>
          </div>
          <select value={muscle} onChange={(e) => setMuscle(e.target.value)} className="h-11 rounded-xl border border-border bg-white px-3 text-sm">
            <option value="">{L.all}</option>
            {MUSCLES.map(m => <option key={m} value={m}>{L.muscles[m] || m}</option>)}
          </select>
          <select value={modality} onChange={(e) => setModality(e.target.value)} className="h-11 rounded-xl border border-border bg-white px-3 text-sm">
            <option value="">{L.all}</option>
            {MODALITIES.map(m => <option key={m} value={m}>{L.modalities[m] || m}</option>)}
          </select>
        </div>

        <p className="text-sm text-stone mb-4">{total} {L.total}</p>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : exercises.length === 0 ? (
          <p className="text-stone italic font-inter">{L.empty}</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {exercises.map(ex => (
              <button key={ex.exercise_id} onClick={() => setSelected(ex)} className="text-left bg-white rounded-2xl shadow-soft overflow-hidden hover:shadow-md transition-shadow">
                {ex.image_url ? (
                  <img src={ex.image_url} alt={ex.name_es} className="w-full h-36 object-cover bg-muted" loading="lazy" />
                ) : (
                  <div className="w-full h-36 bg-muted flex items-center justify-center"><Dumbbell className="w-8 h-8 text-stone" /></div>
                )}
                <div className="p-3">
                  <p className="font-manrope font-semibold text-charcoal text-sm truncate">{language === 'es' ? (ex.name_es || ex.name_en) : (ex.name_en || ex.name_es)}</p>
                  <p className="text-xs text-stone truncate">{(ex.muscle_groups || []).map(m => L.muscles[m] || m).join(', ')}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Detail dialog */}
        <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
          <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-manrope">{language === 'es' ? (selected.name_es || selected.name_en) : (selected.name_en || selected.name_es)}</DialogTitle>
                </DialogHeader>
                {selected.video_url ? (
                  <video controls playsInline poster={selected.image_hd || selected.image_url} className="w-full rounded-2xl bg-muted mb-3">
                    <source src={selected.video_url} type="video/mp4" />
                  </video>
                ) : (
                  (selected.image_hd || selected.image_url) && <img src={selected.image_hd || selected.image_url} alt={selected.name_es} className="w-full rounded-2xl bg-muted mb-3" />
                )}
                <div className="flex flex-wrap gap-2 mb-3">
                  {(selected.muscle_groups || []).map(m => <span key={m} className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">{L.muscles[m] || m}</span>)}
                  {(selected.equipment || []).map(eq => <span key={eq} className="text-xs bg-muted px-3 py-1 rounded-full text-stone">{eq}</span>)}
                  {selected.difficulty && <span className="text-xs bg-sage/10 text-sage px-3 py-1 rounded-full">{selected.difficulty}</span>}
                </div>
                {(language === 'es' ? selected.instructions_es : selected.instructions_en) && (
                  <div className="mb-3">
                    <p className="font-manrope font-semibold text-charcoal mb-1">{L.instructions}</p>
                    <NumberedText text={language === 'es' ? selected.instructions_es : selected.instructions_en} />
                  </div>
                )}
                {(language === 'es' ? selected.common_mistakes_es : selected.common_mistakes_en) && (
                  <div>
                    <p className="font-manrope font-semibold text-charcoal mb-1">{L.mistakes}</p>
                    <NumberedText text={language === 'es' ? selected.common_mistakes_es : selected.common_mistakes_en} />
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
